/**
 * Approve or deny from the phone. Offline, a decision waits in IndexedDB (shared with the
 * service worker, which also flushes it on Background Sync) and is sent when the network
 * is back, unless the request has expired by then: an expired decision is dropped and
 * reported, never sent late.
 */
import type { ApprovalAnswer, ApprovalDecision, ApprovalRequest, ApprovalsState } from '@orgus/protocol';
import { formatClock } from '@orgus/ui';
import { ApiError, apiPost } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';

export interface QueuedDecision {
  approvalId: string;
  answer: ApprovalAnswer;
  title: string;
  expiresAt: number;
  queuedAt: number;
}

export type DecisionOutcome = 'sent' | 'queued';

export const DB_NAME = 'airside';
export const DB_STORE = 'decisions';
export const SYNC_TAG = 'airside-decisions';

/** Split the queue into what may still be sent and what expired while offline. */
export function partitionQueue(items: QueuedDecision[], now: number): { live: QueuedDecision[]; expired: QueuedDecision[] } {
  const live: QueuedDecision[] = [];
  const expired: QueuedDecision[] = [];
  for (const d of items) (d.expiresAt <= now ? expired : live).push(d);
  return { live, expired };
}

export function expiredError(title: string, expiresAt: number): ApiError {
  return new ApiError({
    what: `"${title}" has expired.`,
    why: `It stopped accepting answers at ${formatClock(expiresAt)}, so nothing was sent.`,
    next: 'The session already got its timeout answer. Open it to see what happened.',
  });
}

/** A failure to reach the hub at all (as opposed to the hub refusing). */
const isNetworkError = (e: unknown) => e instanceof ApiError && !e.status;

// ---------------------------------------------------------------------------
// IndexedDB
// ---------------------------------------------------------------------------

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(DB_STORE)) req.result.createObjectStore(DB_STORE, { keyPath: 'approvalId' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const r = fn(db.transaction(DB_STORE, mode).objectStore(DB_STORE));
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
  } finally {
    db.close();
  }
}

export const queued = (): Promise<QueuedDecision[]> => (typeof indexedDB === 'undefined' ? Promise.resolve([]) : tx('readonly', (s) => s.getAll() as IDBRequest<QueuedDecision[]>));
const enqueue = (d: QueuedDecision) => tx('readwrite', (s) => s.put(d));
const dequeue = (id: string) => tx('readwrite', (s) => s.delete(id));

// ---------------------------------------------------------------------------
// Deciding
// ---------------------------------------------------------------------------

/** Demo mode: decide against the store instead of the hub. */
function decideInStore(id: string, answer: ApprovalAnswer, extra: DecisionExtra = {}): void {
  // Through applyExt, so the answer lands in the full state and is not undone by the next update.
  const st = useOrgus.getState();
  const a = st.ext.approvals as ApprovalsState | undefined;
  const hit = a?.pending.find((p) => p.id === id);
  if (!a || !hit) return;
  const done: ApprovalRequest = {
    ...hit,
    state: answer === 'allow' ? 'allowed' : 'denied',
    decidedAt: Date.now(),
    decidedBy: 'owner',
    scope: 'once',
    ...(extra.note ? { note: extra.note } : {}),
    ...(extra.attachmentIds?.length ? { attachmentIds: extra.attachmentIds } : {}),
  };
  st.applyExt({ ext: 'approvals', type: 'state', data: { ...a, pending: a.pending.filter((p) => p.id !== id), recent: [done, ...a.recent].slice(0, 50) } });
}

/** A note and photos or files (uploaded first) that go with a phone answer. */
export interface DecisionExtra {
  note?: string;
  attachmentIds?: string[];
}

export async function decide(a: Pick<ApprovalRequest, 'id' | 'title' | 'expiresAt'>, answer: ApprovalAnswer, now = Date.now(), extra: DecisionExtra = {}): Promise<DecisionOutcome> {
  if (a.expiresAt <= now) throw expiredError(a.title, a.expiresAt);
  if (useOrgus.getState().mode === 'mock') {
    decideInStore(a.id, answer, extra);
    return 'sent';
  }
  const note = extra.note?.trim();
  // The same answer as on every other surface (ADR 0072): a note only when the person wrote one.
  const body: ApprovalDecision = { answer, scope: 'once', ...(note ? { note } : {}), ...(extra.attachmentIds?.length ? { attachmentIds: extra.attachmentIds } : {}) };
  try {
    await apiPost(`/api/approvals/${encodeURIComponent(a.id)}`, body);
    return 'sent';
  } catch (e) {
    // An answer with a note or files is not queued (the queue keeps only the answer): say so now.
    if (!isNetworkError(e) || typeof indexedDB === 'undefined' || note || extra.attachmentIds?.length) throw e;
    await enqueue({ approvalId: a.id, answer, title: a.title, expiresAt: a.expiresAt, queuedAt: now });
    const reg = await navigator.serviceWorker?.getRegistration().catch(() => undefined);
    await (reg as (ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } }) | undefined)?.sync?.register(SYNC_TAG).catch(() => undefined);
    return 'queued';
  }
}

export interface FlushReport {
  sent: QueuedDecision[];
  expired: QueuedDecision[];
  refused: { d: QueuedDecision; why: string }[];
}

/** Send what waited offline. Expired decisions are dropped; refusals (already decided) are reported. */
export async function flushQueue(now = Date.now()): Promise<FlushReport> {
  const report: FlushReport = { sent: [], expired: [], refused: [] };
  const items = await queued().catch(() => []);
  const { live, expired } = partitionQueue(items, now);
  for (const d of expired) {
    await dequeue(d.approvalId);
    report.expired.push(d);
  }
  for (const d of live) {
    try {
      await apiPost(`/api/approvals/${encodeURIComponent(d.approvalId)}`, { answer: d.answer, scope: 'once' } satisfies ApprovalDecision);
      await dequeue(d.approvalId);
      report.sent.push(d);
    } catch (e) {
      if (isNetworkError(e)) break;
      await dequeue(d.approvalId);
      report.refused.push({ d, why: e instanceof ApiError ? e.ui.why : 'The hub refused it.' });
    }
  }
  return report;
}
